import { useAuth } from "@/app/providers/AuthContext";
import {
  routeHelpers,
  isSafeInternalPath,
} from "@/app/routing/routeCategories";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * AuthNavigator handles all authentication-based navigation logic.
 * It runs after AuthProvider and watches auth state + current route,
 * then redirects as needed. This keeps the AuthProvider pure and
 * centralizes all navigation logic in one testable place.
 */
export function AuthNavigator() {
  const { isReady, user, activePracticeId } = useAuth();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Don't navigate until auth state is resolved
    if (!isReady) return;

    // Special-case: periochart subdomain is public and should not trigger auth redirects
    const host = typeof window !== "undefined" ? window.location.host : "";
    const isPerioSubdomain =
      host === "periochart.zermmi.com" || host.startsWith("periochart.");
    const isReviewsSubdomain =
      host === "reviews.zermmi.com" || host.startsWith("reviews.");
    if (isPerioSubdomain && (pathname === "/" || pathname.startsWith("/periochart"))) {
      return;
    }
    if (isReviewsSubdomain && (pathname === "/" || pathname.startsWith("/reviews"))) {
      return;
    }

    const searchParams = new URLSearchParams(search);
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    const hashParams = new URLSearchParams(hash.replace(/^#/, "?"));
    const onResetPasswordPage = routeHelpers.isResetPasswordPage(pathname);
    const onAcceptInvitePage = routeHelpers.isAcceptInvitePage(pathname);

    // Check for Supabase error codes in URL hash (expired/invalid links)
    const hasSupabaseError = hashParams.has("error") || searchParams.has("error");
    const errorCode = hashParams.get("error_code") || searchParams.get("error_code");
    const errorDescription = hashParams.get("error_description") || searchParams.get("error_description") || "";
    const isExpiredLink =
      (hasSupabaseError && errorCode === "401") ||
      errorDescription.toLowerCase().includes("expired") ||
      errorDescription.toLowerCase().includes("invalid");

    const hasInviteAuthParams =
      searchParams.has("code") ||
      hashParams.has("code") ||
      searchParams.has("token_hash") ||
      hashParams.has("token_hash");

    if ((hasInviteAuthParams || isExpiredLink) && !onAcceptInvitePage && !onResetPasswordPage) {
      navigate(`/accept-invite${search}${hash}`, { replace: true });
      return;
    }

    const onAuthPage = routeHelpers.isAuthPage(pathname);
    const onOnboardingPage = routeHelpers.isOnboardingPage(pathname);
    const onProtectedPage = routeHelpers.isProtectedPage(pathname);
    const fromAuth = searchParams.get('from') === 'auth';

    // Centralized redirect predicates
    const shouldRedirectToLogin = !user && onProtectedPage;
    // Allow password reset flow even if user exists but practice is not set
    // Allow invite acceptance page even if user has no active practice
    // Once user has authenticated (user exists), redirect to onboarding if no practice
    // This applies even if they're on an auth page since auth is complete
    const shouldRedirectToOnboarding =
      user &&
      !activePracticeId &&
      !onOnboardingPage &&
      !onResetPasswordPage &&
      !onAcceptInvitePage;
    const shouldRedirectFromOnboarding = user && activePracticeId && onOnboardingPage && !fromAuth;
    // Do not redirect away from /reset-password even when user + practice exist
    // Do not redirect away from /accept-invite (existing users may accept invites)
    const shouldRedirectFromAuth =
      user &&
      activePracticeId &&
      onAuthPage &&
      !onResetPasswordPage &&
      !onAcceptInvitePage;

    // Execute redirects
    if (shouldRedirectToLogin) {
      const next = encodeURIComponent(pathname + search);
      navigate(`/login?next=${next}`, { replace: true });
      return;
    }

    if (shouldRedirectToOnboarding) {
      navigate("/onboarding?from=auth", { replace: true });
      return;
    }

    if (shouldRedirectFromOnboarding) {
      navigate("/", { replace: true });
      return;
    }

    if (shouldRedirectFromAuth) {
      const next = searchParams.get("next");
      const target = isSafeInternalPath(next) && next ? next : "/";
      navigate(target, { replace: true });
      return;
    }
  }, [isReady, user, activePracticeId, pathname, search, navigate]);
  // This component only handles navigation - it doesn't render anything
  return null;
}
